import { useEffect, useState } from "react";
import {
  collection,
  doc,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from "firebase/firestore";
import {
  activeListeners,
  type ListenerDoc,
  type MessageDoc,
  type StationState,
  type TrackDoc,
} from "radio-shared";
import { firestore } from "../firebase";

export type Station = {
  state: StationState | null;
  /** ce qui va passer, imports en cours compris */
  queue: TrackDoc[];
  /** le morceau à l'antenne, ou null si la radio est muette */
  current: TrackDoc | null;
  history: TrackDoc[];
  listeners: ListenerDoc[];
  /** du plus ancien au plus récent, prêt à afficher */
  messages: MessageDoc[];
  loaded: boolean;
};

const docsOf = <T,>(snap: { docs: { id: string; data: () => unknown }[] }): T[] =>
  snap.docs.map((d) => ({ ...(d.data() as T), id: d.id }));

/**
 * L'état de la station, en temps réel. Firestore pousse les changements de
 * lui-même : c'est ce qui nous évite d'écrire et d'exploiter un WebSocket.
 */
export function useStation(nowMs: () => number, signedIn: boolean): Station {
  const [state, setState] = useState<StationState | null>(null);
  const [queue, setQueue] = useState<TrackDoc[]>([]);
  const [playing, setPlaying] = useState<TrackDoc[]>([]);
  const [history, setHistory] = useState<TrackDoc[]>([]);
  const [listeners, setListeners] = useState<ListenerDoc[]>([]);
  const [messages, setMessages] = useState<MessageDoc[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!signedIn) return;
    const tracks = collection(firestore, "tracks");

    const unsubs = [
      onSnapshot(doc(firestore, "station", "state"), (snap) => {
        setState(snap.exists() ? (snap.data() as StationState) : { nowPlaying: null, skipVotes: {}, rev: 0 });
        setLoaded(true);
      }),
      onSnapshot(
        query(tracks, where("state", "in", ["queued", "importing"]), orderBy("queuePos"), limit(100)),
        (snap) => setQueue(docsOf<TrackDoc>(snap)),
      ),
      onSnapshot(query(tracks, where("state", "==", "playing"), limit(2)), (snap) =>
        setPlaying(docsOf<TrackDoc>(snap)),
      ),
      onSnapshot(
        query(tracks, where("state", "==", "played"), orderBy("playedAtMs", "desc"), limit(30)),
        (snap) => setHistory(docsOf<TrackDoc>(snap)),
      ),
      onSnapshot(collection(firestore, "listeners"), (snap) =>
        setListeners(snap.docs.map((d) => ({ ...(d.data() as ListenerDoc), uid: d.id }))),
      ),
      // décroissant côté requête pour garder les *derniers* messages, puis
      // réinversé pour l'affichage
      onSnapshot(
        query(collection(firestore, "messages"), orderBy("sentAtMs", "desc"), limit(100)),
        (snap) => setMessages(docsOf<MessageDoc>(snap).reverse()),
      ),
    ];

    return () => unsubs.forEach((u) => u());
  }, [signedIn]);

  const currentId = state?.nowPlaying?.trackId ?? null;

  return {
    state,
    queue,
    // on croise avec l'état de la station : un doc "playing" orphelin ne doit pas
    // faire croire qu'un son passe alors que la station est muette
    current: currentId ? playing.find((t) => t.id === currentId) ?? null : null,
    history,
    listeners: activeListeners(listeners, nowMs()),
    messages,
    loaded,
  };
}
